import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { FACTOR_CONFIG, LEVEL_LABELS, LEVELS, RISK_CATEGORIES } from "components/risk/constants"
import { WeightSlider } from "components/risk/WeightSlider"

import { riskService } from "services/risk/risk.service"
import { RiskCategory, RiskLevelSettings } from "services/risk/types"

export const RiskLevelsTab = () => {
  const [settings, setSettings] = useState<RiskLevelSettings | null>(null)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    riskService.getRiskLevelSettings().then((res) => {
      if (res) setSettings(res)
    })
  }, [])

  const categoryWeightSum = useMemo(() => {
    if (!settings) return 0
    const weight = settings.category_weights
    return weight.financial + weight.profile + weight.behavioral
  }, [settings])

  const factorWeightSums = useMemo(() => {
    if (!settings) return { financial: 0, profile: 0, behavioral: 0 }
    return {
      financial: Object.values(settings.factor_weights.financial).reduce((s, v) => s + v, 0),
      profile: Object.values(settings.factor_weights.profile).reduce((s, v) => s + v, 0),
      behavioral: Object.values(settings.factor_weights.behavioral).reduce((s, v) => s + v, 0)
    }
  }, [settings])

  const setCategoryWeight = useCallback(
    (cat: RiskCategory, val: number) => {
      if (!settings) return
      setSettings({ ...settings, category_weights: { ...settings.category_weights, [cat]: val } })
      setDirty(true)
    },
    [settings]
  )

  const setFactorWeight = useCallback(
    (cat: RiskCategory, key: string, val: number) => {
      if (!settings) return
      setSettings({
        ...settings,
        factor_weights: {
          ...settings.factor_weights,
          [cat]: { ...settings.factor_weights[cat], [key]: val }
        }
      })
      setDirty(true)
    },
    [settings]
  )

  const setThreshold = useCallback(
    (level: string, field: "min" | "max", val: number) => {
      if (!settings) return
      setSettings({
        ...settings,
        thresholds: {
          ...settings.thresholds,
          [level]: { ...settings.thresholds[level as keyof typeof settings.thresholds], [field]: val }
        }
      })
      setDirty(true)
    },
    [settings]
  )

  const handleSave = async () => {
    if (!settings) return
    if (categoryWeightSum !== 100) {
      toast.error("Category weights must sum to 100%")
      return
    }
    const invalidFactors = RISK_CATEGORIES.filter((c) => factorWeightSums[c] !== 100)
    if (invalidFactors.length > 0) {
      toast.error(`Factor weights for ${invalidFactors.join(", ")} must each sum to 100%`)
      return
    }
    setSaving(true)
    await riskService.updateRiskLevelSettings(settings)
    toast.success("Risk level settings updated")
    setSaving(false)
    setDirty(false)
  }

  if (!settings)
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    )

  return (
    <div className="space-y-6">
      {/* Category Weights */}
      <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-2">Category Weights</h3>
        <p className="text-sm mb-4 text-gray-500 dark:text-gray-400">
          Weights must sum to 100%. Adjust to prioritize different risk categories.
        </p>
        <div className="space-y-3">
          <WeightSlider label="Financial" value={settings.category_weights.financial} onChange={(v) => setCategoryWeight("financial", v)} />
          <WeightSlider label="Profile" value={settings.category_weights.profile} onChange={(v) => setCategoryWeight("profile", v)} />
          <WeightSlider
            label="Behavioral"
            value={settings.category_weights.behavioral}
            onChange={(v) => setCategoryWeight("behavioral", v)}
          />
        </div>
        {categoryWeightSum !== 100 && <p className="text-error text-sm mt-3">⚠ Weights sum to {categoryWeightSum}% — must equal 100%</p>}
      </div>

      {/* Factor Weights per Category */}
      {RISK_CATEGORIES.map((cat) => (
        <div
          key={cat}
          className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg p-6"
        >
          <h3 className="text-lg font-semibold mb-2 capitalize">{cat} Factors</h3>
          <p className="text-sm mb-4 text-gray-500 dark:text-gray-400">
            Weights must sum to 100%. Adjust to prioritize different factors within this category.
          </p>
          <div className="space-y-3">
            {FACTOR_CONFIG[cat].map((factor) => (
              <WeightSlider
                key={factor.key}
                label={factor.label}
                value={settings.factor_weights[cat][factor.key] ?? 0}
                onChange={(v) => setFactorWeight(cat, factor.key, v)}
              />
            ))}
          </div>
          {factorWeightSums[cat] !== 100 && (
            <p className="text-error text-sm mt-3">⚠ Weights sum to {factorWeightSums[cat]}% — must equal 100%</p>
          )}
        </div>
      ))}

      {/* Level Thresholds */}
      <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Risk Level Thresholds</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {LEVELS.map((level) => {
            const meta = LEVEL_LABELS[level]
            const t = settings.thresholds[level]
            return (
              <div key={level} className="rounded-lg p-4 bg-gray-50 dark:bg-[#1a1b2e]">
                <span className={`badge ${meta.cls} badge-sm mb-3`}>{meta.label}</span>
                <div className="flex items-center gap-2">
                  <div className="form-control flex-1">
                    <label className="label py-0">
                      <span className="label-text text-xs">Min</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={t.min}
                      onChange={(e) => setThreshold(level, "min", parseInt(e.target.value, 10) || 0)}
                      className="input input-bordered input-sm dark:bg-[#1a1b2e] dark:border-[rgba(255,255,255,0.1)]"
                    />
                  </div>
                  <span className="self-end pb-2">–</span>
                  <div className="form-control flex-1">
                    <label className="label py-0">
                      <span className="label-text text-xs">Max</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={t.max}
                      onChange={(e) => setThreshold(level, "max", parseInt(e.target.value, 10) || 0)}
                      className="input input-bordered input-sm dark:bg-[#1a1b2e] dark:border-[rgba(255,255,255,0.1)]"
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
        {saving ? "Saving..." : "Save Settings"}
      </button>
    </div>
  )
}
